// src/app/api/http.js
import axios from "axios";
import { getAccessToken, clearSession } from "../state/authSession.js";

const AUTH_LOGIN_PATH = "/auth/login"; // frontend route

function stripTrailingSlashes(s) {
  return (s || "").trim().replace(/\/+$/, "");
}

/**
 * VITE_API_URL examples:
 *   - https://worldcoins-4odu.onrender.com
 *   - https://worldcoins-4odu.onrender.com/api
 *   - https://worldcoins-4odu.onrender.com/api/v1
 *
 * Normalizes to:
 *   ORIGIN: https://worldcoins-4odu.onrender.com
 *   API_BASE_URL: https://worldcoins-4odu.onrender.com/api
 */
function deriveApiBase(viteApiUrl) {
  const raw = stripTrailingSlashes(viteApiUrl);

  // Local fallback
  if (!raw) {
    const origin = "http://127.0.0.1:8000";
    return { origin, apiBase: `${origin}/api` };
  }

  // If env accidentally includes /api or /api/v1, normalize back to origin
  let origin = raw;

  if (origin.endsWith("/api/v1")) origin = origin.slice(0, -7);
  else if (origin.endsWith("/api")) origin = origin.slice(0, -4);

  origin = stripTrailingSlashes(origin);
  return { origin, apiBase: `${origin}/api` };
}

const { origin: ORIGIN, apiBase: API_BASE_URL } = deriveApiBase(
  import.meta.env.VITE_API_URL
);

/**
 * IMPORTANT:
 * - Do NOT set a global "Content-Type: application/json" header.
 *   It breaks multipart/form-data uploads (KYC docs, avatars, etc.).
 */
const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

/**
 * Helpers
 */
function isFormDataBody(data) {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url || "");
}

function toPathOnly(url) {
  try {
    if (!url) return "";
    if (isAbsoluteUrl(url)) return new URL(url).pathname;
    return url; // relative path already
  } catch {
    return url || "";
  }
}

// ------------------------------
// Error normalization (safe UI)
// ------------------------------
function formatValidationDetail(detail) {
  if (!detail) return "";

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    if (detail.length && typeof detail[0] === "object" && detail[0]?.msg) {
      return detail
        .map((e) => {
          const loc = Array.isArray(e.loc) ? e.loc.join(".") : String(e.loc ?? "");
          return loc ? `${loc}: ${e.msg}` : String(e.msg);
        })
        .join(" | ");
    }
    return detail.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join(" | ");
  }

  if (typeof detail === "object") {
    if (detail.msg) return String(detail.msg);
    if (detail.message) return String(detail.message);
    return JSON.stringify(detail);
  }

  return String(detail);
}

function getAxiosUserMessage(error) {
  const data = error?.response?.data;
  const detail = data?.detail ?? data?.message ?? data;
  return formatValidationDetail(detail) || error?.message || "Request failed";
}
// ------------------------------

// ----- Request interceptor: attach token + prevent upload failures -----
http.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (isFormDataBody(config.data)) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
      config.headers.Accept = config.headers.Accept || "application/json";
    } else {
      config.headers.Accept = config.headers.Accept || "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ----- Response interceptor: handle auth expiry safely + attach userMessage -----
http.interceptors.response.use(
  (response) => response,
  (error) => {
    error.userMessage = getAxiosUserMessage(error);

    const status = error?.response?.status;
    const requestUrl = error?.config?.url || "";
    const path = toPathOnly(requestUrl);

    const isAuthCall =
      path.includes("/auth/login") ||
      path.includes("/auth/register") ||
      path.includes("/auth/forgot-password") ||
      path.includes("/auth/reset") ||
      path.includes("/auth/dev-login");

    const isPublicCall =
      path.includes("/health") ||
      path.includes("/docs") ||
      path.includes("/openapi.json") ||
      path === "/" ||
      path.includes("/favicon.ico");

    if (status === 401 && !isAuthCall && !isPublicCall) {
      clearSession();

      if (window.location.pathname !== AUTH_LOGIN_PATH) {
        const from = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `${AUTH_LOGIN_PATH}?from=${from}`;
      }
    }

    return Promise.reject(error);
  }
);

export default http;
export { ORIGIN, API_BASE_URL };
